import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center font-medium tracking-[-1.5px] uppercase tracking-[0.1em] transition-all duration-200 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-spicenet-primary text-spicenet-black hover:bg-spicenet-primary/90 active:bg-spicenet-primary/80",
        secondary:
          "bg-spicenet-dark text-white hover:bg-spicenet-dark/80 active:bg-spicenet-black",
        monochrome:
          "bg-white text-spicenet-black hover:bg-white/90 active:bg-white/80",
      },
      size: {
        default: "h-14 px-12 py-4 text-base",
        sm: "h-10 px-8 py-2 text-sm",
        lg: "h-16 px-16 py-5 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    // Determine corner color based on variant
    const getCornerColor = () => {
      if (variant === "primary") return "bg-spicenet-black"; // Black corners for red button
      if (variant === "secondary") return "bg-white"; // White corners for dark button
      if (variant === "monochrome") return "bg-spicenet-black"; // Black corners for white button
      return "bg-spicenet-black"; // Default to black
    };

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {/* Corners - always present with padding */}
        <>
          {/* Top-left corner */}
          <span
            className={cn(
              "absolute top-1 left-1 w-1.5 h-1.5",
              getCornerColor()
            )}
          />
          {/* Top-right corner */}
          <span
            className={cn(
              "absolute top-1 right-1 w-1.5 h-1.5",
              getCornerColor()
            )}
          />
          {/* Bottom-left corner */}
          <span
            className={cn(
              "absolute bottom-1 left-1 w-1.5 h-1.5",
              getCornerColor()
            )}
          />
          {/* Bottom-right corner */}
          <span
            className={cn(
              "absolute bottom-1 right-1 w-1.5 h-1.5",
              getCornerColor()
            )}
          />
        </>
        <span className="relative z-10 flex items-center tracking-[0px]">{props.children}</span>
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
